import type { RoutineView } from '../../types';

/**
 * Get sibling step kind lookup for a given step using the RoutineView derived
 * data.  Returns `null` when siblings cannot be determined (e.g. the step is
 * a root-level step or derived data is missing).
 *
 * The returned object exposes:
 * - `index` – the step's position among its siblings
 * - `kindAt(i)` – a helper that returns the stepKind for the sibling at index `i`
 */
export function getSiblingStepKinds(
  routine: RoutineView,
  stepId: string,
): { index: number; kindAt: (i: number) => string | undefined } | null {
  const { derived } = routine;
  if (!derived) return null;

  const parents = derived.parentsById[stepId];

  // Determine the sibling list.
  // If the step has a parent, use the parent's children.
  // If the step is root-level, use the top-level `routine.steps`.
  let siblingIds: string[];

  if (parents && parents.length > 0) {
    const immediateParentId = parents[0].id;
    const parentStep = derived.stepById[immediateParentId];
    if (!parentStep) return null;
    siblingIds = parentStep.children ?? [];
  } else {
    // Root-level step — siblings are other steps with no parent.
    // Since routine.steps is a flat list of ALL steps, filter for those
    // that have no parent (empty chain) and preserve their original order.
    siblingIds = routine.steps
      .filter((s) => {
        const p = derived.parentsById[s.id];

        return !p || p.length === 0;
      })
      .map((s) => s.id);
  }

  const index = siblingIds.indexOf(stepId);
  if (index === -1) return null;

  const kindAt = (i: number): string | undefined => {
    const id = siblingIds[i];
    if (id == null) return undefined;

    return derived.stepById[id]?.stepKind;
  };

  return { index, kindAt };
}

/**
 * Return the index of `stepId` among its parent's children (or among the
 * top-level steps if root). Returns -1 when not found.
 */
export function getSiblingPosition(
  routine: RoutineView,
  stepId: string,
): number {
  const sib = getSiblingStepKinds(routine, stepId);

  return sib?.index ?? -1;
}
